/** The signed-in caller's identity + effective access, fetched once from /api/me and provided to
 * the whole SPA so navigation and action buttons can be gated. Server routes enforce the same
 * rules independently — this is only for the UI. */
import { createContext, useContext } from 'react'
import type { AccessLevel, MeResponse } from '../types'

export const DEFAULT_ME: MeResponse = { email: '', displayName: '', isAdmin: false, grants: {}, services: [] }

export const MeContext = createContext<MeResponse>(DEFAULT_ME)

export function useMe(): MeResponse {
  return useContext(MeContext)
}

/** The caller's level on a service: admin is always 'manager'; otherwise the explicit grant. */
export function levelForService(me: MeResponse, serviceId: string): AccessLevel | null {
  if (me.isAdmin) return 'manager'
  return me.grants[serviceId] ?? null
}

export function canAccessService(me: MeResponse, serviceId: string): boolean {
  return levelForService(me, serviceId) !== null
}

export function canManageService(me: MeResponse, serviceId: string): boolean {
  return levelForService(me, serviceId) === 'manager'
}

/** True when the user has no access at all (no admin, no grants, no grantable service). */
export function hasAnyAccess(me: MeResponse): boolean {
  return me.isAdmin || Object.keys(me.grants).length > 0
}
